import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Confetti from "react-confetti";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Heart, Home, ArrowRight } from "lucide-react";

const DonationSuccess = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showConfetti, setShowConfetti] = useState(true);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const amount = searchParams.get("amount") || "0";
  const reference = searchParams.get("ref");

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);

    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 5000);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timer);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 flex items-center justify-center p-4">
      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={500}
          gravity={0.3}
        />
      )}

      <Card className="w-full max-w-lg">
        <CardContent className="pt-12 pb-8 px-8">
          <div className="text-center space-y-6">
            {/* Success Icon */}
            <div className="relative inline-block">
              <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto animate-pulse">
                <CheckCircle className="h-12 w-12 text-white" />
              </div>
              <div className="absolute -top-2 -right-2 w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center animate-bounce">
                <Heart className="h-6 w-6 text-white fill-white" />
              </div>
            </div>

            {/* Title */}
            <div>
              <h1 className="text-3xl font-bold mb-2">{t("donateSuccess.title")}</h1>
              <p className="text-lg text-muted-foreground">
                {t("donateSuccess.subtitle")}
              </p>
            </div>

            {/* Amount Display */}
            <div className="bg-gradient-to-br from-pink-500/10 to-purple-500/10 rounded-lg p-6 border border-pink-200/50">
              <p className="text-sm text-muted-foreground mb-1">
                {t("donateSuccess.donationAmount")}
              </p>
              <p className="text-4xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
                ${amount}
              </p>
            </div>

            {/* Message */}
            <p className="text-muted-foreground">
              {t("donateSuccess.message")}
            </p>

            {/* Reference */}
            {reference && (
              <div className="text-xs text-muted-foreground">
                <span>{t("donateSuccess.reference")}: </span>
                <span className="font-mono">{reference}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => navigate("/donate")}
                className="flex-1"
              >
                <Heart className="mr-2 h-4 w-4" />
                {t("donateSuccess.anotherDonation")}
              </Button>
              <Button
                onClick={() => navigate("/")}
                className="flex-1"
              >
                <Home className="mr-2 h-4 w-4" />
                {t("donateSuccess.goHome")}
              </Button>
            </div>

            {/* Receipt Note */}
            <p className="text-sm text-muted-foreground">
              {t("donateSuccess.receiptSent")}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DonationSuccess;
